import leafletImage from "leaflet-image";

import { attachProgressUI } from "./progress.js";
import { fitBoundsAndWait } from "./map.js";

const A4 = {
  portrait: { wMm: 210, hMm: 297 },
  landscape: { wMm: 297, hMm: 210 },
};
const EXPORT_PADDING_RATIO = 0.003;
const IMAGE_CROP_PADDING_RATIO = 0.01;
const MARGIN_MM = 10;
const DPI = 300;
const WATERMARK_TEXT = "nailthetrail.com/map";

export async function exportMapImage({
  root,
  map,
  mapEl,
  currentGpxLayer,
  lastGpxBounds,
  orientation,
  onRestore,
}) {
  const progress = attachProgressUI(root.querySelector(".lgpxp__mapContainer"));
  let cancelled = false;
  progress.show();
  progress.offCancel();
  // progress.onCancel(() => { cancelled = true; });

  const step = (label, pct) => {
    progress.text(label);
    progress.pct(pct);
  };
  const bump = (pct) => progress.pct(pct);

  const page = A4[orientation] || A4.portrait;
  const usableWmm = page.wMm - 2 * MARGIN_MM;
  const usableHmm = page.hMm - 2 * MARGIN_MM;
  const exportW = mmToPx(usableWmm);
  const exportH = mmToPx(usableHmm);

  const prevW = mapEl.style.width;
  const prevH = mapEl.style.height;

  function restore() {
    mapEl.style.width = prevW;
    mapEl.style.height = prevH;
    map.invalidateSize(true);
    onRestore?.();
  }

  try {
    if (cancelled) throw new Error("cancelled");

    step("Resizing map...", 5);
    mapEl.style.width = exportW + "px";
    mapEl.style.height = exportH + "px";
    await delay(50);
    map.invalidateSize(true);

    if (cancelled) throw new Error("cancelled");

    const bounds =
      lastGpxBounds || (currentGpxLayer && currentGpxLayer.getBounds());
    if (bounds && bounds.isValid()) {
      step("Fitting bounds...", 12);
      await fitBoundsAndWait(map, bounds, {
        padding: [
          Math.round(exportW * EXPORT_PADDING_RATIO),
          Math.round(exportH * EXPORT_PADDING_RATIO),
        ],
      });
    }

    if (cancelled) throw new Error("cancelled");

    step("Loading tiles...", 15);
    await waitTilesLoadedWithProgress(map, ({ done, total }) => {
      if (total > 0) {
        const local = done / total;
        bump(15 + Math.round(local * 55));
        return;
      }

      bump(25);
    });

    if (cancelled) throw new Error("cancelled");

    step("Finalizing view...", 75);
    await delay(50);

    step("Rendering image...", 82);
    await renderAndDownloadMap({
      map,
      bounds,
      exportW,
      exportH,
      usableWmm,
      orientation,
      step,
      bump,
    });

    step("Done", 100);
  } catch (e) {
    if (e && e.message === "cancelled") {
      progress.text("Cancelled");
      progress.pct(0);
    } else {
      alert("Export failed; see console.");
      console.error(e);
      progress.text("Failed");
      progress.pct(0);
    }
  } finally {
    setTimeout(() => progress.hide(), 400);
    restore();
  }
}

function renderAndDownloadMap({
  map,
  bounds,
  exportW,
  exportH,
  usableWmm,
  orientation,
  step,
  bump,
}) {
  return new Promise((resolve, reject) => {
    leafletImage(map, (err, canvas) => {
      if (err) return reject(err);

      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = exportW;
      exportCanvas.height = exportH;
      const ctx = exportCanvas.getContext("2d");
      const crop = getBoundsCrop({ map, bounds, exportW, exportH });

      ctx.drawImage(
        canvas,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        exportW,
        exportH
      );
      drawScaleBar({
        ctx,
        map,
        exportW,
        exportH,
        usableWmm,
        imageScale: exportW / crop.width,
      });
      drawWatermark({ ctx, exportW, exportH, usableWmm });

      bump(90);
      step("Encoding PNG...", 92);

      const filename = `map_A4_${orientation}_10mm.png`;
      if (exportCanvas.toBlob) {
        exportCanvas.toBlob((blob) => {
          if (!blob) {
            downloadDataUrl(exportCanvas.toDataURL("image/png"), filename);
            bump(98);
            resolve();
            return;
          }

          step("Saving...", 96);
          downloadBlob(blob, filename);
          resolve();
        }, "image/png");
        return;
      }

      downloadDataUrl(exportCanvas.toDataURL("image/png"), filename);
      bump(98);
      resolve();
    });
  });
}

function getBoundsCrop({ map, bounds, exportW, exportH }) {
  if (!bounds || !bounds.isValid()) {
    return { x: 0, y: 0, width: exportW, height: exportH };
  }

  const northWest = map.latLngToContainerPoint(bounds.getNorthWest());
  const southEast = map.latLngToContainerPoint(bounds.getSouthEast());
  const padding = Math.round(Math.min(exportW, exportH) * IMAGE_CROP_PADDING_RATIO);

  let minX = Math.min(northWest.x, southEast.x) - padding;
  let maxX = Math.max(northWest.x, southEast.x) + padding;
  let minY = Math.min(northWest.y, southEast.y) - padding;
  let maxY = Math.max(northWest.y, southEast.y) + padding;
  let width = maxX - minX;
  let height = maxY - minY;

  if (width <= 0 || height <= 0) {
    return { x: 0, y: 0, width: exportW, height: exportH };
  }

  const targetAspect = exportW / exportH;
  const cropAspect = width / height;
  const centerX = minX + width / 2;
  const centerY = minY + height / 2;

  if (cropAspect > targetAspect) {
    height = width / targetAspect;
  } else {
    width = height * targetAspect;
  }

  if (width > exportW || height > exportH) {
    return { x: 0, y: 0, width: exportW, height: exportH };
  }

  minX = centerX - width / 2;
  minY = centerY - height / 2;
  minX = Math.max(0, Math.min(minX, exportW - width));
  minY = Math.max(0, Math.min(minY, exportH - height));

  return {
    x: Math.round(minX),
    y: Math.round(minY),
    width: Math.round(width),
    height: Math.round(height),
  };
}

function drawScaleBar({ ctx, map, exportW, exportH, usableWmm, imageScale }) {
  try {
    const lat = map.getCenter().lat;
    const zoom = map.getZoom();
    const metersPerPixel =
      (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
    const pxPerMm = exportW / usableWmm;
    const metersPerMm = (metersPerPixel / imageScale) * pxPerMm;
    const targetMm = 40;
    const targetMeters = metersPerMm * targetMm;
    const pow10 = Math.pow(10, Math.floor(Math.log10(targetMeters)));
    const barMeters =
      [1, 2, 5]
        .map((x) => x * pow10)
        .filter((candidate) => candidate <= targetMeters)
        .pop() ||
      pow10 / 2;
    const barMm = barMeters / metersPerMm;
    const barPx = Math.round(barMm * pxPerMm);

    const x0 = Math.round(6 * pxPerMm);
    const y0 = exportH - Math.round(8 * pxPerMm);
    const hBar = Math.max(1, Math.round(2 * pxPerMm));

    ctx.save();
    ctx.fillStyle = "#000000";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = Math.max(0.5, Math.round(0.2 * pxPerMm));
    ctx.fillRect(x0, y0 - hBar, Math.floor(barPx / 2), hBar);
    ctx.strokeRect(
      x0 + Math.floor(barPx / 2),
      y0 - hBar,
      Math.ceil(barPx / 2),
      hBar
    );

    const label =
      barMeters >= 1000
        ? (barMeters % 1000 === 0
            ? (barMeters / 1000).toFixed(0)
            : (barMeters / 1000).toFixed(1)) + " km"
        : barMeters + " m";

    ctx.font = `${Math.max(8, Math.round(6 * pxPerMm))}px sans-serif`;
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#000000";
    ctx.fillText(label, x0, y0 - hBar - Math.round(1.5 * pxPerMm));
    ctx.restore();
  } catch (e) {
    console.warn("Scale bar draw failed:", e);
  }
}

function drawWatermark({ ctx, exportW, exportH, usableWmm }) {
  try {
    const pxPerMm = exportW / usableWmm;
    const fontSize = Math.max(10, Math.round(3.5 * pxPerMm));
    const padX = Math.round(2.5 * pxPerMm);
    const padY = Math.round(1.5 * pxPerMm);
    const margin = Math.round(5 * pxPerMm);
    const radius = Math.round(1.5 * pxPerMm);

    ctx.save();
    ctx.font = `${fontSize}px sans-serif`;
    ctx.textBaseline = "middle";

    const textWidth = ctx.measureText(WATERMARK_TEXT).width;
    const width = Math.ceil(textWidth + padX * 2);
    const height = Math.ceil(fontSize + padY * 2);
    const x = exportW - width - margin;
    const y = exportH - height - margin;

    ctx.fillStyle = "rgba(255, 255, 255, 0.82)";
    drawRoundedRect(ctx, x, y, width, height, radius);
    ctx.fill();

    ctx.fillStyle = "rgba(0, 0, 0, 0.82)";
    ctx.fillText(WATERMARK_TEXT, x + padX, y + height / 2);
    ctx.restore();
  } catch (e) {
    console.warn("Watermark draw failed:", e);
  }
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);

  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function waitTilesLoadedWithProgress(map, onProgress) {
  return new Promise((resolve) => {
    let total = 0;
    let done = 0;
    let finished = false;
    const handlers = [];

    function bind(layer) {
      const start = () => {
        total++;
        onProgress?.({ done, total });
      };
      const complete = () => {
        done++;
        onProgress?.({ done, total });
        check();
      };

      layer.on("tileloadstart", start);
      layer.on("tileload", complete);
      layer.on("tileerror", complete);
      handlers.push([layer, start, complete]);
    }

    function check() {
      if (!finished && total > 0 && done >= total) finish();
    }

    function finish() {
      if (finished) return;
      finished = true;
      handlers.forEach(([layer, start, complete]) => {
        layer.off("tileloadstart", start);
        layer.off("tileload", complete);
        layer.off("tileerror", complete);
      });
      resolve();
    }

    map.eachLayer((layer) => {
      if (layer && layer._tiles) bind(layer);
    });

    let initialCounted = false;
    map.eachLayer((layer) => {
      if (!layer || !layer._tiles) return;
      const tiles = Object.values(layer._tiles);
      if (!initialCounted) {
        total += tiles.length;
        initialCounted = true;
      }
      tiles.forEach((tile) => {
        if (tile.loaded) done++;
      });
    });

    onProgress?.({ done, total });
    if (total === 0 || done >= total) setTimeout(finish, 50);
    setTimeout(finish, 5000);
  });
}

function downloadBlob(blob, filename) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(a.href);
  a.remove();
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mmToPx(mm) {
  return Math.round((mm * DPI) / 25.4);
}
